import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock3, Instagram, Mail, MapPin, MessageSquare, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { getBackendOrigin, getImageUrl } from '@/utils/imagePaths';

type CompanyPublicInfo = {
  name: string;
  address: string;
  phone: string;
  whatsapp: string;
  email: string;
  instagram: string;
  cuit: string;
  weekdayHours: string;
  saturdayHours: string;
  logo: string | null;
};

type ContactFormState = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  website: string;
};

const defaultCompanyInfo: CompanyPublicInfo = {
  name: 'ROCAM',
  address: '',
  phone: '',
  whatsapp: '',
  email: '',
  instagram: '',
  cuit: '',
  weekdayHours: '',
  saturdayHours: '',
  logo: null
};

const defaultFormState: ContactFormState = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  website: ''
};

const ROCAM_CONTACT = {
  address: 'Oroño 620, San Lorenzo',
  phone: '3476 432564',
  mapsLink: 'https://maps.app.goo.gl/QHZCskMnwFVxp6aj7',
  mapsEmbedUrl: 'https://www.google.com/maps?q=-32.7352009,-60.7341654&z=17&output=embed'
} as const;

const cleanPhone = (value: string) => value.replace(/[^\d+]/g, '');

const resolveInstagramUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `https://instagram.com/${value.replace(/^@/, '')}`;
};

const resolveLogoUrl = (value?: string | null) => {
  if (!value) return '';
  if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:')) {
    return value;
  }

  const normalized = value.replace(/\\/g, '/').replace(/^\/+/, '');
  if (normalized.startsWith('uploads/')) {
    return getImageUrl(`/${normalized}`);
  }

  return `${getBackendOrigin()}/api/settings/company/logo/${encodeURIComponent(normalized)}`;
};

const ContactPublic: React.FC = () => {
  const [company, setCompany] = useState<CompanyPublicInfo>(defaultCompanyInfo);
  const [form, setForm] = useState<ContactFormState>(defaultFormState);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await axios.get('/api/settings/company/public');
        setCompany({ ...defaultCompanyInfo, ...(response.data || {}) });
      } catch (error) {
        console.error('Error loading public company info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompany();
  }, []);

  const handleChange =
    (field: keyof ContactFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((current) => ({
        ...current,
        [field]: event.target.value
      }));
    };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSending(true);

    try {
      const response = await axios.post('/api/contact', form);
      toast.success(response.data?.message || 'Consulta enviada correctamente.');
      setForm(defaultFormState);
    } catch (error: any) {
      const message =
        error?.response?.data?.error || 'No se pudo enviar el mensaje. Intenta nuevamente.';
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  };

  const logoUrl = resolveLogoUrl(company.logo);
  const displayAddress = ROCAM_CONTACT.address;
  const displayPhone = ROCAM_CONTACT.phone;
  const mapsEmbedUrl = ROCAM_CONTACT.mapsEmbedUrl;
  const mapsLink = ROCAM_CONTACT.mapsLink;
  const whatsappDigits = cleanPhone(company.whatsapp);
  const whatsappLink = whatsappDigits ? `https://wa.me/${whatsappDigits.replace(/^\+/, '')}` : '';
  const instagramLink = resolveInstagramUrl(company.instagram);
  const contactCards = [
    {
      title: 'Ubicación',
      value: displayAddress,
      icon: MapPin,
      href: mapsLink
    },
    {
      title: 'Teléfono fijo',
      value: displayPhone,
      icon: Phone,
      href: `tel:${cleanPhone(displayPhone)}`
    },
    {
      title: 'WhatsApp',
      value: company.whatsapp || 'Configurar WhatsApp desde ajustes',
      icon: MessageSquare,
      href: whatsappLink
    },
    {
      title: 'Email',
      value: company.email || 'Configurar email desde ajustes',
      icon: Mail,
      href: company.email ? `mailto:${company.email}` : ''
    }
  ];

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(244,63,94,0.18),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(245,158,11,0.18),_transparent_28%),linear-gradient(180deg,_#fff8f1_0%,_#ffffff_38%,_#f5f5f4_100%)] text-slate-900">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <section className="overflow-hidden rounded-[32px] border border-white/70 bg-white/85 shadow-[0_30px_120px_-45px_rgba(15,23,42,0.45)] backdrop-blur">
          <div className="grid gap-0 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="relative overflow-hidden bg-[#111827] px-6 py-10 text-white sm:px-10 lg:px-12 lg:py-14">
              <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(248,113,113,0.16),transparent_40%,rgba(251,191,36,0.18))]" />
              <div className="absolute -left-20 top-16 h-56 w-56 rounded-full bg-rose-500/20 blur-3xl" />
              <div className="absolute bottom-0 right-0 h-64 w-64 translate-x-16 translate-y-16 rounded-full bg-amber-400/20 blur-3xl" />

              <div className="relative z-10 max-w-2xl">
                <div className="mb-6 flex items-center gap-4">
                  {logoUrl ? (
                    <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-white/15 bg-white/95 p-3 shadow-lg">
                      <img src={logoUrl} alt={company.name} className="h-full w-full object-contain" />
                    </div>
                  ) : null}
                  <div>
                    <p className="text-xs uppercase tracking-[0.28em] text-white/60">ROCAM</p>
                    <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                      Contacto directo con el taller
                    </h1>
                  </div>
                </div>

                <p className="max-w-xl text-base leading-7 text-slate-200 sm:text-lg">
                  {isLoading
                    ? 'Cargando información de contacto...'
                    : 'Encuentra nuestra información de contacto, horarios, ubicación y un formulario para escribirnos desde el sitio.'}
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {contactCards.map(({ title, value, icon: Icon, href }) => (
                    <a
                      key={title}
                      href={href || undefined}
                      target={href?.startsWith('http') ? '_blank' : undefined}
                      rel={href?.startsWith('http') ? 'noreferrer' : undefined}
                      className="group rounded-2xl border border-white/10 bg-white/8 p-4 transition hover:border-white/25 hover:bg-white/12"
                    >
                      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-amber-300">
                        <Icon className="h-5 w-5" />
                      </div>
                      <p className="text-sm uppercase tracking-[0.18em] text-white/45">{title}</p>
                      <p className="mt-2 text-base font-medium leading-6 text-white">{value}</p>
                    </a>
                  ))}
                </div>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/8 p-5">
                    <div className="mb-3 flex items-center gap-3 text-amber-300">
                      <Clock3 className="h-5 w-5" />
                      <span className="text-sm uppercase tracking-[0.18em] text-white/55">Horarios</span>
                    </div>
                    <div className="space-y-2 text-sm leading-6 text-slate-200">
                      <p>
                        <span className="font-semibold text-white">Lunes a viernes:</span>{' '}
                        {company.weekdayHours || 'A definir'}
                      </p>
                      <p>
                        <span className="font-semibold text-white">Sábados:</span>{' '}
                        {company.saturdayHours || 'A definir'}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/8 p-5">
                    <div className="mb-3 flex items-center gap-3 text-amber-300">
                      <Instagram className="h-5 w-5" />
                      <span className="text-sm uppercase tracking-[0.18em] text-white/55">Redes</span>
                    </div>
                    <div className="space-y-2 text-sm leading-6 text-slate-200">
                      <p>{company.instagram || 'Instagram no configurado'}</p>
                      <div className="flex flex-wrap gap-3 pt-2">
                        {instagramLink ? (
                          <a
                            href={instagramLink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                          >
                            Ver Instagram
                          </a>
                        ) : null}
                        {whatsappLink ? (
                          <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-sm font-medium text-emerald-200 transition hover:bg-emerald-400/20"
                          >
                            Escribir por WhatsApp
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#f8fafc] px-5 py-6 sm:px-8 lg:px-10 lg:py-10">
              <Card className="overflow-hidden border-slate-200/80 shadow-sm">
                <CardContent className="p-0">
                  <div className="border-b border-slate-200/80 px-6 py-5">
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-500">Ubicación</p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                      Cómo llegar
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{displayAddress}</p>
                  </div>
                  <iframe
                    title="Ubicación ROCAM"
                    src={mapsEmbedUrl}
                    loading="lazy"
                    className="h-[320px] w-full border-0"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="border-t border-slate-200/80 px-6 py-5">
                    <a
                      href={mapsLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                    >
                      Abrir en Google Maps
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="border-t border-slate-200/80 bg-[#f8fafc] px-5 py-6 sm:px-8 lg:px-10 lg:py-10">
            <Card className="border-slate-200/80 shadow-sm">
              <CardContent className="p-6 sm:p-7">
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-500">Formulario</p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                    Envíanos tu consulta
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    El mensaje se enviará a la casilla de la empresa configurada en el sistema.
                  </p>
                </div>

                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700" htmlFor="contact-name">
                        Nombre
                      </label>
                      <Input
                        id="contact-name"
                        placeholder="Tu nombre"
                        value={form.name}
                        onChange={handleChange('name')}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700" htmlFor="contact-email">
                        Email
                      </label>
                      <Input
                        id="contact-email"
                        type="email"
                        placeholder="nombre@empresa.com"
                        value={form.email}
                        onChange={handleChange('email')}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700" htmlFor="contact-phone">
                        Teléfono
                      </label>
                      <Input
                        id="contact-phone"
                        placeholder="Opcional"
                        value={form.phone}
                        onChange={handleChange('phone')}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700" htmlFor="contact-subject">
                        Asunto
                      </label>
                      <Input
                        id="contact-subject"
                        placeholder="Consulta por servicio"
                        value={form.subject}
                        onChange={handleChange('subject')}
                      />
                    </div>
                  </div>

                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={handleChange('website')}
                    autoComplete="off"
                    tabIndex={-1}
                    className="hidden"
                    aria-hidden="true"
                  />

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700" htmlFor="contact-message">
                      Mensaje
                    </label>
                    <textarea
                      id="contact-message"
                      className="min-h-[150px] w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                      placeholder="Cuéntanos en qué podemos ayudarte"
                      value={form.message}
                      onChange={handleChange('message')}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSending}
                    className="h-11 w-full rounded-xl bg-slate-900 text-white hover:bg-slate-800"
                  >
                    <Send className="h-4 w-4" />
                    {isSending ? 'Enviando...' : 'Enviar consulta'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactPublic;
